import { Link } from '@/components/Link';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { LoyaltyPoints } from '@/components/LoyaltyPoints';
import { NumberInput } from '@/components/NumberInput';
import { ProductThumbnail } from '@/components/ProductThumbnail';
import { useCheckout } from '@/contexts/checkoutContext';
import { useDataLayer } from '@/hooks/useDataLayer';
import type { OrderItem } from '@/types';
import { getIsBook } from '@/utils/getIsBook';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as Styled from './CartOrdersList.styled';
import { useUpdateQuantity } from './useUpdateQuantity';

const useDeferredPending = (isPending: boolean, delay = 250) => {
  const [showSpinner, setShowSpinner] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (isPending) {
      timerRef.current = setTimeout(() => setShowSpinner(true), delay);
    } else {
      clearTimeout(timerRef.current);
      setShowSpinner(false);
    }
    return () => clearTimeout(timerRef.current);
  }, [isPending, delay]);

  return showSpinner;
};

interface CartOrdersListProps {
  isBasketPage?: boolean;
  orders: OrderItem[];
}

export const CartOrdersList = ({
  isBasketPage = false,
  orders,
}: CartOrdersListProps) => {
  const { checkoutData } = useCheckout();
  const { isPending, removeItem, updateQuantity } = useUpdateQuantity();
  const showSpinner = useDeferredPending(isPending);
  const { removeFromCart } = useDataLayer();

  const handleDelete = useCallback(
    (orderItem: OrderItem) => () => {
      // Find the item in the cart to get full product data
      const cartItem = checkoutData.items?.find(
        (item: OrderItem) => item.sku === orderItem.sku,
      );

      if (cartItem) {
        // Track removal before executing
        removeFromCart(cartItem);
      }

      removeItem({ code: orderItem.sku, isCheckoutSession: false });
    },
    [checkoutData.items, removeItem, removeFromCart],
  );

  const handleUpdate = useCallback(
    (orderItem: OrderItem) => (quantity: number) => {
      if (quantity === 0) {
        // Find the item in the cart to get full product data
        const cartItem = checkoutData.items?.find(
          (item: OrderItem) => item.sku === orderItem.sku,
        );

        if (cartItem) {
          // Track removal when quantity goes to zero
          removeFromCart(cartItem);
        }

        removeItem({ code: orderItem.sku, isCheckoutSession: false });
      } else {
        updateQuantity({
          code: orderItem.sku,
          isCheckoutSession: false,
          quantity,
        });
      }
    },
    [checkoutData.items, removeItem, updateQuantity, removeFromCart],
  );

  return (
    <Styled.List>
      {orders.map(orderItem => {
        const {
          author,
          image,
          loyaltyPoints,
          maxQuantity,
          price,
          name,
          productType,
          productUrl,
          promoBadge,
          quantity,
          sku,
        } = orderItem;

        return (
          <Styled.Item key={sku}>
            {isBasketPage && productUrl ? (
              <Link href={productUrl}>
                <ProductThumbnail
                  image={image}
                  isBook={getIsBook(productType)}
                  promoBadge={promoBadge}
                  quantity={isBasketPage ? undefined : quantity}
                />
              </Link>
            ) : (
              <ProductThumbnail
                image={image}
                isBook={getIsBook(productType)}
                promoBadge={promoBadge}
                quantity={isBasketPage ? undefined : quantity}
              />
            )}
            <Styled.ItemContentWrapper>
              <Styled.Description>
                <Styled.Title>
                  {isBasketPage && productUrl ? (
                    <Styled.TitleLink href={productUrl}>
                      {name}
                    </Styled.TitleLink>
                  ) : (
                    name
                  )}
                </Styled.Title>
                <Styled.Price>
                  <span className="currentPrice">{price?.current}</span>
                  {price?.former ? (
                    <span className="formerPrice">{price.former}</span>
                  ) : null}
                </Styled.Price>
                {author ? <Styled.ByLine>{author}</Styled.ByLine> : null}
                {loyaltyPoints ? (
                  <LoyaltyPoints points={loyaltyPoints} />
                ) : null}
              </Styled.Description>
              {isBasketPage ? (
                <Styled.PendingWrapper $isPending={isPending}>
                  {showSpinner && (
                    <Styled.SpinnerOverlay>
                      <LoadingSpinner />
                    </Styled.SpinnerOverlay>
                  )}
                  <Styled.QuantityWrapper>
                    <NumberInput
                      canDelete={true}
                      isDisabled={isPending}
                      label="quantity"
                      maxValue={maxQuantity}
                      minValue={0}
                      onChange={handleUpdate(orderItem)}
                      value={quantity}
                    />
                    <Link
                      handler={handleDelete(orderItem)}
                      isButton={true}
                      isDisabled={isPending}
                    >
                      Delete
                    </Link>
                  </Styled.QuantityWrapper>
                </Styled.PendingWrapper>
              ) : null}
            </Styled.ItemContentWrapper>
          </Styled.Item>
        );
      })}
    </Styled.List>
  );
};
